import { onUnmounted, ref, toRaw, unref, watch } from 'vue';
import type { FormActionType } from '@mts/components/Form';
import type { FetchParams, PaginationProps, TableActionType } from '@mts/components/Table';
import type { DynamicProps, Nullable, Recordable } from '@mts-fe/types';
import type { Key } from 'ant-design-vue/es/table/interface';
import type { Ref, WatchStopHandle } from 'vue';
import { getDynamicProps } from '@mts-fe/utils';
import { error, mtsEmitter } from '@mts/utils';
import { IN_PROD, PADMode } from '@mts/utils/env';
import type { BasicColumn, BasicTableProps, FormTableAction } from '../types/table';

export function useTable(
  tableProps?: Partial<DynamicProps<BasicTableProps>>,
): [(instance: TableActionType, formInstance: FormActionType) => void, FormTableAction] {
  const tableRef = ref<Nullable<TableActionType>>(null);
  const loadedRef = ref<Nullable<boolean>>(false);
  const formRef = ref<Nullable<FormActionType>>(null);

  let stopWatch: WatchStopHandle;

  function register(instance: TableActionType, formInstance: FormActionType) {
    IN_PROD &&
      onUnmounted(() => {
        tableRef.value = null;
        loadedRef.value = null;
      });

    if (unref(loadedRef) && IN_PROD && instance === unref(tableRef)) return;

    tableRef.value = instance;
    formRef.value = formInstance;
    tableProps && instance.setProps(getDynamicProps(tableProps));
    loadedRef.value = true;

    // if (PADMode) return;

    stopWatch?.();

    stopWatch = watch(
      () => tableProps,
      () => {
        tableProps && instance.setProps(getDynamicProps(tableProps));
      },
      {
        immediate: true,
        deep: true,
      },
    );

    /** 监听多语言变更 */
    mtsEmitter.on('localeChanged', () => {
      tableProps && instance.setProps(getDynamicProps(tableProps));
    });
  }

  return [register, getTableMethods(tableRef, formRef)];
}

export const getTableMethods = (
  tableRef: Ref<Nullable<TableActionType>>,
  formRef: Ref<Nullable<FormActionType>>,
): FormTableAction => {
  function getTable() {
    const table = unref(tableRef);
    if (!table) {
      error(
        'The table instance has not been obtained yet, please make sure the table is presented when performing the table operation!',
      );
    }
    return table as TableActionType;
  }

  return {
    getForm: () => {
      return unref(formRef) as unknown as FormActionType;
    },

    reload: async (opt?: FetchParams) => {
      return await getTable().reload(opt);
    },
    setProps: (props: Partial<BasicTableProps>, isInner?: boolean) => {
      getTable().setProps(props, isInner);
    },
    redoHeight: () => {
      getTable().redoHeight();
    },
    setSelectedRows: (rows: Recordable[]) => {
      return toRaw(getTable().setSelectedRows(rows));
    },
    setLoading: (loading: boolean) => {
      getTable().setLoading(loading);
    },
    getDataSource: () => {
      return getTable().getDataSource();
    },
    getRawDataSource: () => {
      return getTable().getRawDataSource();
    },
    getSearchInfo: () => {
      return getTable().getSearchInfo();
    },
    getColumns: ({ ignoreIndex = false, ...args }: { ignoreIndex?: boolean } = {}) => {
      const columns = getTable().getColumns({ ignoreIndex, ...args }) || [];
      return toRaw(columns);
    },
    setColumns: (columns: BasicColumn[], isInner?: boolean) => {
      getTable().setColumns(columns, isInner);
    },
    setCacheColumnsByField: (dataIndex: string | undefined, value: Partial<BasicColumn>) => {
      getTable().setCacheColumnsByField?.(dataIndex, value);
    },
    setTableData: (values: any[]) => {
      return getTable().setTableData(values);
    },
    setPagination: (info: Partial<PaginationProps>) => {
      return getTable().setPagination(info);
    },
    deleteSelectRowByKey: (keyValue: Key) => {
      getTable().deleteSelectRowByKey(keyValue);
    },
    getSelectRowKeys: () => {
      return toRaw(getTable().getSelectRowKeys());
    },
    getSelectRows: () => {
      return toRaw(getTable().getSelectRows());
    },
    clearSelectedRowKeys: () => {
      getTable().clearSelectedRowKeys();
    },
    setSelectedRowKeys: (keyValues: Key[]) => {
      getTable().setSelectedRowKeys(keyValues);
    },
    getPaginationRef: () => {
      return getTable().getPaginationRef();
    },
    getProps: () => {
      return toRaw(getTable().getProps());
    },
    getSize: () => {
      return toRaw(getTable().getSize());
    },
    updateTableData: (index: number, key: string, value: any) => {
      return getTable().updateTableData(index, key, value);
    },
    validateTableData: async () => {
      return await getTable().validateTableData();
    },
    deleteTableDataRecord: (keyValues: Key | Key[]) => {
      return getTable().deleteTableDataRecord(keyValues);
    },
    insertTableDataRecord: (
      record: Recordable | Recordable[],
      index?: number,
      delCount?: number,
    ) => {
      return getTable().insertTableDataRecord(record, index, delCount);
    },
    updateTableDataRecord: (keyValue: Key, record: Recordable, append?: boolean) => {
      return getTable().updateTableDataRecord(keyValue, record, append);
    },
    findTableDataRecord: (keyValue: Key) => {
      return getTable().findTableDataRecord(keyValue);
    },
    getRowSelection: () => {
      return toRaw(getTable().getRowSelection());
    },
    getCacheColumns: () => {
      return toRaw(getTable().getCacheColumns());
    },
    setShowPagination: async (show: boolean) => {
      getTable().setShowPagination(show);
    },
    getShowPagination: () => {
      return toRaw(getTable().getShowPagination());
    },
    toggleActionColumn: () => {
      return toRaw(getTable().toggleActionColumn());
    },
    expandAll: () => {
      getTable().expandAll();
    },
    collapseAll: () => {
      getTable().collapseAll();
    },
    expandRows: (keyValues: Key[]) => {
      getTable().expandRows(keyValues);
    },
    collapseRows: (keyValues: Key[]) => {
      getTable().collapseRows(keyValues);
    },
    scrollTo: (pos: string) => {
      getTable().scrollTo(pos);
    },
  };
};
